/**
 * API Error Middleware
 * Intercepts RTK Query errors and displays user-friendly messages
 * Based on Flow project error handling pattern
 */

import {
  MiddlewareAPI,
  isRejectedWithValue,
  Middleware,
} from '@reduxjs/toolkit';
import i18n from '../../../i18n/i18n.module';
import { createStandaloneToast } from '@chakra-ui/react';

interface FieldError {
  field: string;
  reason: string;
  message: string;
}

interface DomainError {
  code: string;
  domain: string;
  message: string;
  statusCode: number;
  errors?: FieldError[];
}

interface StandardError {
  code: string;
  message: string;
  statusCode: number;
  details?: string;
  field?: string;
  domain?: string;
}

interface ErrorResponse {
  data?: DomainError | StandardError | {
    error?: DomainError | StandardError;
  };
  status?: number;
}

/**
 * RTK Query Error Logger Middleware
 * Catches all rejected RTK Query actions and displays error messages
 */
export const apiErrorMiddleware: Middleware =
  (api: MiddlewareAPI) => (next) => (action) => {
    // Check if this is a rejected action with a value
    if (isRejectedWithValue(action)) {
      const payload = action.payload as ErrorResponse;

      // 401 is handled globally by baseQueryWithReauth (logout + redirect).
      // Skip the toast here to avoid a flash before the page navigates away.
      if (payload?.status === 401) {
        return next(action);
      }
      
      console.log('API Error Middleware caught error:', {
        action,
        payload,
        payloadData: payload?.data,
      });

      // Extract error data from response
      // Backend can return error in multiple formats:
      // 1. Domain-aware: { code, domain, message, statusCode }
      // 2. Standard: { code, message, statusCode, field }
      // 3. Wrapped: { error: { code, message, statusCode, field } }
      let errorData: DomainError | StandardError | undefined;
      
      if (payload?.data && typeof payload.data === 'object' && payload.data !== null) {
        // Check if data has error property (wrapped format)
        if ('error' in payload.data && payload.data.error) {
          errorData = payload.data.error as DomainError | StandardError;
        } 
        // Check if data is direct error object
        else if ('code' in payload.data && 'statusCode' in payload.data) {
          errorData = payload.data as DomainError | StandardError;
        }
      }

      console.log('Extracted error data:', errorData);

      // Type guard: Check if errorData is DomainError
      const isDomainError = (error: DomainError | StandardError | undefined): error is DomainError => {
        return error !== undefined && typeof error === 'object' && error !== null && 'domain' in error && error.domain !== undefined;
      };

      // Type guard: Check if errorData is StandardError with field
      const isStandardError = (error: DomainError | StandardError | undefined): error is StandardError => {
        return error !== undefined && typeof error === 'object' && error !== null && 'field' in error && error.field !== undefined && !('domain' in error);
      };

      // Handle domain-aware errors (new structured error format)
      if (isDomainError(errorData)) {
        handleDomainError(errorData, api);
      } 
      // Handle standard errors with field (e.g., DUPLICATE_ENTRY with field)
      else if (isStandardError(errorData)) {
        handleStandardError(errorData, api);
      } 
      else {
        // Handle legacy/generic errors
        handleGenericError(payload, api);
      }
    }

    return next(action);
  };

// Create standalone toast instance (works outside React component tree)
const { toast } = createStandaloneToast();

/**
 * Handle domain-aware structured errors
 * Uses i18n translation based on domain and code
 * Supports field-level errors for form validation
 */
function handleDomainError(error: DomainError, api: MiddlewareAPI): void {
  const { code, domain, errors: fieldErrors } = error;
  
  console.log('Domain error caught:', {
    code,
    domain,
    fieldErrors,
  });

  // If there are field-level errors, this is a validation error
  // The component should handle it via .catch() and setFieldError
  // We still show a toast for user feedback
  if (fieldErrors && fieldErrors.length > 0) {
    // For field-level errors, show first field error message
    const firstError = fieldErrors[0];
    const translatedMessage = i18n.t(`errors.${domain.toLowerCase()}.${firstError.reason}`, {
      defaultValue: firstError.message,
    });

    console.log('Field-level error:', {
      field: firstError.field,
      reason: firstError.reason,
      message: translatedMessage,
    });

    // Show toast notification
    toast({
      title: translatedMessage,
      status: 'error',
      duration: 8000,
      isClosable: true,
      position: 'bottom',
    });
    
    return;
  }

  // For non-field errors, use the main error message
  // Build i18n key: errors.{domain}.{code}
  const domainLower = domain.toLowerCase();
  const codeParts = code.split('_').slice(1); // Remove domain prefix
  const codeKey = codeParts
    .map((part, index) => 
      index === 0 ? part.toLowerCase() : part.charAt(0) + part.slice(1).toLowerCase()
    )
    .join(''); // Convert to camelCase

  const i18nKey = `errors.${domainLower}.${codeKey}`;
  
  console.log('Non-field error i18n lookup:', {
    domain,
    code,
    i18nKey,
  });

  // Try to get translation with fallback
  const translated = i18n.t(i18nKey, {
    defaultValue: i18n.t('errors.generic', {
      defaultValue: error.message || 'An error occurred. Please try again.',
    }),
  });

  console.log('Translated message:', translated);

  // Show Chakra UI toast notification
  toast({
    title: translated,
    status: 'error',
    duration: 8000,
    isClosable: true,
    position: 'bottom',
  });
}

/**
 * Handle generic/legacy errors
 * Fallback for errors without domain/code structure
 */
function handleGenericError(payload: ErrorResponse, api: MiddlewareAPI): void {
  const errorData = payload?.data;
  const statusCode = payload?.status;

  console.log('Generic error handling:', {
    errorData,
    statusCode,
  });

  // Try to extract message from various possible locations
  const message =
    (errorData as any)?.message ||
    (errorData as any)?.error?.message ||
    getStatusCodeMessage(statusCode);

  // Show Chakra UI toast notification
  toast({
    title: message,
    status: 'error',
    duration: 5000,
    isClosable: true,
    position: 'bottom',
  });
}

/**
 * Handle standard errors with field (e.g., DUPLICATE_ENTRY)
 * Shows field-specific error messages
 */
function handleStandardError(error: StandardError, api: MiddlewareAPI): void {
  const { code, message, field, statusCode } = error;
  
  console.log('Standard error caught:', {
    code,
    field,
    message,
    statusCode,
  });

  // Build user-friendly message based on field and code
  let translatedMessage = message;
  
  if (code === 'DUPLICATE_ENTRY') {
    if (field === 'phone') {
      translatedMessage = i18n.t('auth.sign-up.phone-already-registered', {
        defaultValue: 'This phone number is already registered. Please use another phone number.',
      });
    } else if (field === 'email') {
      translatedMessage = i18n.t('auth.sign-up.email-already-registered', {
        defaultValue: 'This email is already registered. Please sign in or use another email.',
      });
    } else {
      translatedMessage = i18n.t('auth.sign-up.already-registered', {
        defaultValue: message || 'This value already exists. Please use another one.',
      });
    }
  } else {
    // Try to translate using code
    const i18nKey = `errors.${code.toLowerCase()}`;
    translatedMessage = i18n.t(i18nKey, {
      defaultValue: message || 'An error occurred. Please try again.',
    });
  }

  console.log('Translated standard error message:', translatedMessage);

  // Show Chakra UI toast notification
  toast({
    title: translatedMessage,
    status: 'error',
    duration: 8000,
    isClosable: true,
    position: 'bottom',
  });
}

/**
 * Get user-friendly message based on HTTP status code
 */
function getStatusCodeMessage(statusCode?: number): string {
  switch (statusCode) {
    case 400:
      return i18n.t('errors.badRequest', {
        defaultValue: 'Invalid request',
      });
    case 401:
      return i18n.t('errors.unauthorized', {
        defaultValue: 'You are not authorized to perform this action',
      });
    case 403:
      return i18n.t('errors.forbidden', {
        defaultValue: 'Access denied',
      });
    case 404:
      return i18n.t('errors.notFound', {
        defaultValue: 'Resource not found',
      });
    case 409:
      return i18n.t('errors.conflict', {
        defaultValue: 'Conflict occurred',
      });
    case 422:
      return i18n.t('errors.validation', {
        defaultValue: 'Validation error',
      });
    case 500:
      return i18n.t('errors.serverError', {
        defaultValue: 'Server error. Please try again later.',
      });
    default:
      return i18n.t('errors.generic', {
        defaultValue: 'An error occurred. Please try again.',
      });
  }
}
